

import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import "./DisplayBooks.css";

const DisplayBooks = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [books, setBooks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDetails, setOpenDetails] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [selectedBook, setSelectedBook] = useState(null);
  const [formData, setFormData] = useState({});

  // ================= SEARCH (ADDED ONLY) =================
  const [search, setSearch] = useState("");

  // ================= FETCH ALL =================
  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await fetch(`${API_URL}/books`);
      const result = await response.json();
      setBooks(result.data || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

  // ================= SEARCH BOOKS (ADDED ONLY) =================
  const handleSearch = async (value) => {
    setSearch(value);

    if (!value) {
      fetchBooks();
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/books/search?q=${value}`
      );
      const result = await response.json();
      setBooks(result.data || []);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  // ================= PAGINATION =================
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ================= OPEN DIALOGS =================
  const handleOpenDetails = (book) => {
    setSelectedBook(book);
    setOpenDetails(true);
  };

  const handleOpenEdit = (book) => {
    setSelectedBook(book);
    setFormData(book);
    setOpenEdit(true);
  };

  const handleOpenDelete = (book) => {
    setSelectedBook(book);
    setOpenDelete(true);
  };

  const handleCloseDialogs = () => {
    setOpenDetails(false);
    setOpenEdit(false);
    setOpenDelete(false);
    setSelectedBook(null);
    setFormData({});
  };

  // ================= INPUT CHANGE =================
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ================= UPDATE BOOK =================
  const handleUpdate = async () => {
    try {
      const response = await fetch(
        `${API_URL}/books/${selectedBook.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setBooks((prev) =>
          prev.map((b) => (b.id === selectedBook.id ? result.data : b))
        );
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      handleCloseDialogs();
    }
  };

  // ================= DELETE BOOK =================
  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${API_URL}/books/${selectedBook.id}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        setBooks((prev) =>
          prev.filter((b) => b.id !== selectedBook.id)
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      handleCloseDialogs();
    }
  };

  return (
    <>
    <div className="search-box">
      {/* ================= SEARCH INPUT (ADDED ONLY) ================= */}
      <TextField
        fullWidth
        label="Search books..."
        value={search}
        onChange={(e) => handleSearch(e.target.value)}
        sx={{ mb: 2 }}
      />
      </div>

      {/* ================= TABLE ================= */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Pages</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {books
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((book) => (
                <TableRow key={book.id}>
                  <TableCell>{book.id}</TableCell>
                  <TableCell>{book.titleAm}</TableCell>
                  <TableCell>{book.author}</TableCell>
                  <TableCell>{book.category}</TableCell>
                  <TableCell>{book.pages}</TableCell>

                  <TableCell
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenEdit(book)}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleOpenDelete(book)}
                    >
                      Delete
                    </Button>

                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => handleOpenDetails(book)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={books.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* ================= DETAILS DIALOG ================= */}
      <Dialog open={openDetails} onClose={handleCloseDialogs}>
        <DialogTitle>Book Details</DialogTitle>
        <DialogContent>
          <Typography><b>ID:</b> {selectedBook?.id}</Typography>
          <Typography><b>Title:</b> {selectedBook?.title}</Typography>
          <Typography><b>Title Am:</b> {selectedBook?.titleAm}</Typography>
          <Typography><b>Author:</b> {selectedBook?.author}</Typography>
          <Typography><b>Category:</b> {selectedBook?.category}</Typography>
          <Typography><b>Pages:</b> {selectedBook?.pages}</Typography>
          <Typography><b>PDF:</b> {selectedBook?.pdfUrl}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ================= EDIT DIALOG ================= */}
      <Dialog open={openEdit} onClose={handleCloseDialogs}>
        <DialogTitle>Edit Book</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            name="title"
            value={formData.title || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />

          <TextField
            label="Title Am"
            name="titleAm"
            value={formData.titleAm || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />

          <TextField
            label="Author"
            name="author"
            value={formData.author || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />

          <TextField
            label="Category"
            name="category"
            value={formData.category || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />

          <TextField
            label="Pages"
            name="pages"
            value={formData.pages || ""}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialogs} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= DELETE DIALOG ================= */}
      <Dialog open={openDelete} onClose={handleCloseDialogs}>
        <DialogTitle>Delete Book</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <b>{selectedBook?.title}</b>?
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DisplayBooks;

